import { lazy, Suspense, type ReactNode } from 'react';
import type { RoleModule } from '../types';
import { SearchWidthLock } from './components/SearchWidthLock';

/**
 * The reception surface's routes and door overrides. See ../types.ts.
 * Every route path here must start with `/reception/`.
 *
 * The Reception desk IS the Billing desk (the canvas treats front desk and
 * money as one desk; the backend's Billing role maps here too). Billing's own
 * screens land under /reception/billing/… in a later pass; until then its
 * door keeps its default.
 */
/* The Queue speaks the desk's language: English, or Arabic right-to-left
   (`?lang=ar`, kept for the browser). One door, two directions. */
const ReceptionQueueRoute = lazy(() =>
  import('./queue/QueueLanguage').then((m) => ({ default: m.ReceptionQueueRoute })),
);

const ReceptionRecallsPage = lazy(() =>
  import('./recalls/ReceptionRecallsPage').then((m) => ({ default: m.ReceptionRecallsPage })),
);

const ReceptionSchedulePage = lazy(() =>
  import('./schedule/ReceptionSchedulePage').then((m) => ({ default: m.ReceptionSchedulePage })),
);

const ReceptionAvailabilityPage = lazy(() =>
  import('./schedule/ReceptionAvailabilityPage').then((m) => ({ default: m.ReceptionAvailabilityPage })),
);

const ReceptionPatientsPage = lazy(() =>
  import('./patients/ReceptionPatientsPage').then((m) => ({ default: m.ReceptionPatientsPage })),
);

const ReceptionElementsPage = lazy(() =>
  import('./patients/ReceptionElementsPage').then((m) => ({ default: m.ReceptionElementsPage })),
);
const ReceptionScanPage = lazy(() =>
  import('./patients/ReceptionScanPage').then((m) => ({ default: m.ReceptionScanPage })),
);

const BillingOverviewPage = lazy(() =>
  import('./billing/BillingOverviewPage').then((m) => ({ default: m.BillingOverviewPage })),
);

const BillingBillsPage = lazy(() =>
  import('./billing/BillingBillsPage').then((m) => ({ default: m.BillingBillsPage })),
);

const BillingNewBillPage = lazy(() =>
  import('./billing/BillingNewBillPage').then((m) => ({ default: m.BillingNewBillPage })),
);

const BillingAddServicePage = lazy(() =>
  import('./billing/BillingAddServicePage').then((m) => ({ default: m.BillingAddServicePage })),
);
const BillingNewItemPage = lazy(() =>
  import('./billing/BillingAddServicePage').then((m) => ({ default: m.BillingNewItemPage })),
);

const BillingClaimsPage = lazy(() =>
  import('./billing/BillingClaimsPage').then((m) => ({ default: m.BillingClaimsPage })),
);

const BillingCatalogPage = lazy(() =>
  import('./billing/BillingCatalogPage').then((m) => ({ default: m.BillingCatalogPage })),
);

const BillingInsurersPage = lazy(() =>
  import('./billing/BillingInsurersPage').then((m) => ({ default: m.BillingInsurersPage })),
);

const BillingInsurerNewPage = lazy(() =>
  import('./billing/BillingInsurerNewPage').then((m) => ({ default: m.BillingInsurerNewPage })),
);

const RecordPaymentPage = lazy(() =>
  import('./billing/RecordPaymentPage').then((m) => ({ default: m.RecordPaymentPage })),
);
const BillingDebtsPage = lazy(() =>
  import('./billing/BillingDebtsPage').then((m) => ({ default: m.BillingDebtsPage })),
);

const BillingDayClosePage = lazy(() =>
  import('./billing/BillingDayClosePage').then((m) => ({ default: m.BillingDayClosePage })),
);

const ReceptionMessagesPage = lazy(() =>
  import('./messages/ReceptionMessagesPage').then((m) => ({ default: m.ReceptionMessagesPage })),
);

/* Every reception screen's top-bar search keeps its width while typed in. */
const page = (el: ReactNode) => (
  <Suspense fallback={null}>
    <SearchWidthLock />
    {el}
  </Suspense>
);

const reception: RoleModule = {
  routes: [
    { path: '/reception/queue', element: page(<ReceptionQueueRoute />) },
    /* Under /queue on purpose: Recalls is reached from the queue's stat, and
       the Queue door stays lit while it is open (as the board draws it). */
    { path: '/reception/queue/recalls', element: page(<ReceptionRecallsPage />) },
    { path: '/reception/schedule', element: page(<ReceptionSchedulePage />) },
    { path: '/reception/schedule/availability', element: page(<ReceptionAvailabilityPage />) },
    { path: '/reception/patients', element: page(<ReceptionPatientsPage />) },
    /* No rail door of its own: elements belong to a patient and are reached
       through the profile's "Elements & dispatch ▸" — the Patients door stays lit. */
    { path: '/reception/patients/:patientId/elements', element: page(<ReceptionElementsPage />) },
    /* Elements ▸ Scan & confirm (g_p6): what the patient handed over, read and filed on them. */
    { path: '/reception/patients/:patientId/scan', element: page(<ReceptionScanPage />) },
    /* The desk IS the billing desk — the money screens live under it. */
    { path: '/reception/billing', element: page(<BillingOverviewPage />) },
    { path: '/reception/billing/bills', element: page(<BillingBillsPage />) },
    { path: '/reception/billing/bills/new', element: page(<BillingNewBillPage />) },
    { path: '/reception/billing/bills/new/add-service', element: page(<BillingAddServicePage />) },
    { path: '/reception/billing/claims', element: page(<BillingClaimsPage />) },
    { path: '/reception/billing/catalog/new', element: page(<BillingNewItemPage />) },
    { path: '/reception/billing/catalog', element: page(<BillingCatalogPage />) },
    { path: '/reception/billing/insurers', element: page(<BillingInsurersPage />) },
    { path: '/reception/billing/insurers/new', element: page(<BillingInsurerNewPage />) },
    /* Not tabs of their own — reached from the Overview's two doors. */
    { path: '/reception/billing/record-payment', element: page(<RecordPaymentPage />) },
    { path: '/reception/billing/debts', element: page(<BillingDebtsPage />) },
    { path: '/reception/billing/day-close', element: page(<BillingDayClosePage />) },
    /* The Messages centre runs on the product's own inbox endpoints. */
    { path: '/reception/messages', element: page(<ReceptionMessagesPage />) },
  ],
  doorRoutes: {
    queue: '/reception/queue',
    schedule: '/reception/schedule',
    patients: '/reception/patients',
    billing: '/reception/billing',
    messages: '/reception/messages',
  },
};

export default reception;
